import type { App, DirectiveBinding } from "vue";
import { toCamelCase } from "@/utils/textFormatters";

interface InputWithHandler extends HTMLInputElement {
  _camelcaseHandler?: (event: Event) => void;
}

export function initDirectives(app: App<Element>) {
  app.directive("camelcase", {
    mounted(el: HTMLElement, binding: DirectiveBinding) {
      const isRealtime = binding.modifiers.realtime;
      const input =
        el.tagName === "INPUT" || el.tagName === "TEXTAREA"
          ? (el as HTMLInputElement)
          : (el.querySelector("input, textarea") as HTMLInputElement);

      if (!input) {
        return;
      }

      const applyFormat = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const originalValue = target.value;
        const formattedValue = toCamelCase(originalValue);

        if (originalValue !== formattedValue) {
          target.value = formattedValue;
          target.dispatchEvent(new Event("input", { bubbles: true }));

          if (isRealtime && start !== null && end !== null) {
            const diff = formattedValue.length - originalValue.length;
            target.setSelectionRange(start + diff, end + diff);
          }
        }
      };

      (input as InputWithHandler)._camelcaseHandler = applyFormat;

      if (isRealtime) {
        input.addEventListener("input", applyFormat);
      } else {
        input.addEventListener("blur", applyFormat);
      }
    },

    unmounted(el: HTMLElement) {
      const input =
        el.tagName === "INPUT" || el.tagName === "TEXTAREA"
          ? (el as HTMLInputElement)
          : (el.querySelector("input, textarea") as HTMLInputElement);

      const inputWithHandler = input as InputWithHandler;
      if (input && inputWithHandler._camelcaseHandler) {
        input.removeEventListener("input", inputWithHandler._camelcaseHandler);
        input.removeEventListener("blur", inputWithHandler._camelcaseHandler);
        delete inputWithHandler._camelcaseHandler;
      }
    },
  });
}
